import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";
import type { FormField } from "../../components/AdminDataModal";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";

interface Template {
  id: number;
  template_name: string;
}

interface RegionOption {
  id: number;
  region_name: string;
}

interface SMTPOption {
  id: number;
  from_email: string;
}

interface JobOption {
  id: number;
  job_title: string;
}

interface StatusOption {
  status: string;
}

interface FailedEmailItem {
  email: string;
  reason?: string;
}

interface Campaign {
  id: number;
  campaign_name: string;
  email_template_id: number;
  template_name?: string;
  audience: string;
  job_id?: number | null;
  job_title: string;
  application_status?: string | null;
  smtp_from_email: string;
  region_id?: number | null;
  region_name?: string;
  total_recipients?: number;
  sent_emails?: string[];
  failed_emails?: FailedEmailItem[];
  delivery_status: string;
  status?: string;
  created_at?: string;
}

const toCaptionCase = (value: string) => {
  if (!value) return "";
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export default function SendCampaign() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();

  const [data, setData] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [smtpAccounts, setSmtpAccounts] = useState<SMTPOption[]>([]);
  const [jobs, setJobs] = useState<JobOption[]>([]);
  const [jobStatuses, setJobStatuses] = useState<StatusOption[]>([]);
  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [selectedRow, setSelectedRow] = useState<Campaign | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const [loading, setLoading] = useState(false);

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const toggleDropdown = (id: string) => {
    setOpenDropdownId((prev) => (prev === id ? null : id));
  };

  /* =========================
     FETCH CAMPAIGNS
  ========================= */

  const fetchCampaigns = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/admin/email-settings/email-campaigns`,
        {
          credentials: "include",
        },
      );

      const result = await res.json();
      const freshData = result.data || [];
      console.log(freshData);

      if (result.success) {
        setData(freshData);
        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Campaigns Error:", error);
    }
  };

  /* =========================
     FETCH TEMPLATES
  ========================= */

  const fetchTemplates = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/admin/email-settings/email-templates`,
        {
          credentials: "include",
        },
      );

      const result = await res.json();

      if (result.success) {
        setTemplates(result.data || []);
      }
    } catch (error) {
      console.error("Fetch Templates Error:", error);
    }
  };

  /* =========================
     FETCH SMTP
  ========================= */

  const fetchSMTPAccounts = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/email-settings/smtp`, {
        credentials: "include",
      });

      const result = await res.json();

      if (result.success) {
        setSmtpAccounts(result.data || []);
      }
    } catch (error) {
      console.error("Fetch SMTP Accounts Error:", error);
    }
  };

  /* =========================
     FETCH JOBS
  ========================= */

  const fetchJobs = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/admin/email-settings/career-jobs-for-campaign`,
        {
          credentials: "include",
        },
      );

      const result = await res.json();

      if (result.success) {
        setJobs(result.data || []);
      }
    } catch (error) {
      console.error("Fetch Jobs Error:", error);
    }
  };

  /* =========================
     FETCH STATUS BY JOB
  ========================= */

  const fetchStatusesByJob = async (jobId: number | string) => {
    if (!jobId) {
      setJobStatuses([]);
      return;
    }

    try {
      const res = await fetch(
        `${BASE_URL}/admin/email-settings/career-application-statuses/${jobId}`,
        {
          credentials: "include",
        },
      );

      const result = await res.json();
      console.log(result);
      if (result.success) {
        setJobStatuses(result.data || []);
      } else {
        setJobStatuses([]);
      }
    } catch (error) {
      console.error("Fetch Job Statuses Error:", error);
      setJobStatuses([]);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchSMTPAccounts();
    fetchJobs();
    fetchRegions();
  }, []);

  useEffect(() => {
    if (
      modalOpen &&
      activeRow?.audience === "job_applicants" &&
      activeRow?.job_id
    ) {
      fetchStatusesByJob(activeRow.job_id);
    } else {
      setJobStatuses([]);
    }
  }, [modalOpen, activeRow?.audience, activeRow?.job_id]);

  /* =========================
     FETCH REGIONS
  ========================= */

  const fetchRegions = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/admin/email-settings/regions-for-campaign`,
        {
          credentials: "include",
        },
      );

      const result = await res.json();

      if (result.success) {
        setRegions(result.data || []);
      }
    } catch (error) {
      console.error("Fetch Regions Error:", error);
    }
  };

  /* =========================
     FORM FIELDS
  ========================= */

  const campaignFields: FormField[] = useMemo(() => {
    const fields: FormField[] = [
      { key: "id", label: "ID", colSpan: 2 },

      { key: "campaign_name", label: "Campaign Name", colSpan: 2 },

      {
        key: "email_template_id",
        label: "Email Template",
        type: "select",
        options: templates.map((template) => ({
          value: String(template.id),
          label: template.template_name,
        })),
        colSpan: 2,
      },

      {
        key: "smtp_id",
        label: "Email From",
        type: "select",
        options: smtpAccounts
          .filter((smtp: any) => smtp.status === "active")
          .map((smtp) => ({
            value: String(smtp.id),
            label: smtp.from_email,
          })),
        colSpan: 2,
      },

      {
        key: "audience",
        label: "Audience",
        type: "select",
        options: [
          { label: "Subscribers", value: "subscribers" },
          { label: "Users", value: "users" },
          { label: "Public", value: "public" },
          { label: "Job Applicants", value: "job_applicants" },
        ],
        colSpan: 2,
      },
    ];

    if (activeRow?.audience === "public") {
      fields.push({
        key: "region_id",
        label: "Regions",
        type: "select",
        options: regions.map((region) => ({
          value: String(region.id),
          label: region.region_name,
        })),
        colSpan: 2,
      });
    }

    if (activeRow?.audience === "job_applicants") {
      fields.push(
        {
          key: "job_id",
          label: "Job Applications",
          type: "select",
          options: jobs.map((job) => ({
            value: String(job.id),
            label: job.job_title,
          })),
          colSpan: 1,
        },
        {
          key: "application_status",
          label: "Application Status",
          type: "select",
          options: jobStatuses.map((item) => ({
            value: item.status,
            label: toCaptionCase(item.status),
          })),
          colSpan: 1,
        },
      );
    }

    fields.push(
      {
        key: "delivery_status",
        label: "Delivery Status",
        type: "select",
        options: [
          { label: "Draft", value: "draft" },
          { label: "Sent", value: "sent" },
          { label: "Failed", value: "failed" },
        ],
        colSpan: 2,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    );

    return fields;
  }, [
    templates,
    smtpAccounts,
    jobs,
    regions,
    jobStatuses,
    activeRow?.audience,
  ]);

  const columns = [
    { key: "campaign_name", label: "Campaign Name" },
    { key: "template_name", label: "Template" },
    { key: "audience", label: "Audience" },
    { key: "delivery_status", label: "Delivery Status" },
    { key: "created_at", label: "Created At" },
    { key: "status", label: "Status" },
  ];

  /* =========================
     SAVE CAMPAIGN
  ========================= */

  const handleSaveCampaign = async (
    formData: any,
    e?: React.MouseEvent<HTMLButtonElement> | React.FormEvent,
  ) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (formData.audience === "job_applicants" && !formData.job_id) {
      showModal({
        type: "warning",
        title: "Missing Fields",
        message: "Please select Job Applications.",
        confirmText: "OK",
      });
      return;
    }

    if (formData.audience === "public" && !formData.region_id) {
      showModal({
        type: "warning",
        title: "Missing Fields",
        message: "Please select Region.",
        confirmText: "OK",
      });
      return;
    }

    if (
      formData.audience === "job_applicants" &&
      !formData.application_status
    ) {
      showModal({
        type: "warning",
        title: "Missing Fields",
        message: "Please select Application Status.",
        confirmText: "OK",
      });
      return;
    }

    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/email-settings/email-campaigns`
          : `${BASE_URL}/admin/email-settings/email-campaigns/${activeRow.id}`;

      console.log("Save campaign URL:", url);
      console.log("Save campaign method:", method);

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          campaign_name: formData.campaign_name,
          email_template_id: formData.email_template_id,
          smtp_id: formData.smtp_id ? Number(formData.smtp_id) : null,
          audience: formData.audience,
          region_id:
            formData.audience === "public" ? Number(formData.region_id) : null,
          job_id:
            formData.audience === "job_applicants"
              ? Number(formData.job_id)
              : null,
          application_status:
            formData.audience === "job_applicants"
              ? formData.application_status
              : null,
          delivery_status: formData.delivery_status || "draft",
          status: formData.status || "active",
        }),
      });

      const result = await res.json();

      if (result.success) {
        fetchCampaigns();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Campaign saved successfully."
              : "Campaign updated successfully.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Save Campaign Error:", error);
    }
  };

  /* =========================
     SEND CAMPAIGN
  ========================= */

  const handleSendCampaign = async (formData: any) => {
    if (!formData.campaign_name || !formData.email_template_id) {
      showModal({
        type: "warning",
        title: "Missing Fields",
        message: "Please fill all required fields.",
        confirmText: "OK",
      });
      return;
    }

    if (formData.audience === "public" && !formData.region_id) {
      showModal({
        type: "warning",
        title: "Missing Fields",
        message: "Please select Region.",
        confirmText: "OK",
      });
      return;
    }

    if (formData.audience === "job_applicants" && !formData.job_id) {
      showModal({
        type: "warning",
        title: "Missing Fields",
        message: "Please select Job Applications.",
        confirmText: "OK",
      });
      return;
    }

    if (
      formData.audience === "job_applicants" &&
      !formData.application_status
    ) {
      showModal({
        type: "warning",
        title: "Missing Fields",
        message: "Please select Application Status.",
        confirmText: "OK",
      });
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(
        `${BASE_URL}/admin/email-settings/send-campaign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            id: formData.id || null,
            campaign_name: formData.campaign_name,
            template_id: Number(formData.email_template_id),
            smtp_id: formData.smtp_id ? Number(formData.smtp_id) : null,
            audience: formData.audience,
            job_id:
              formData.audience === "job_applicants"
                ? Number(formData.job_id)
                : null,
            application_status:
              formData.audience === "job_applicants"
                ? formData.application_status
                : null,
            delivery_status: "sent",
            status: formData.status || "active",
          }),
        },
      );

      const result = await res.json();

      if (result.success) {
        fetchCampaigns();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message: "Campaign sent successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Campaign failed.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Send Campaign Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Failed to send campaign.",
        confirmText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     DELETE
  ========================= */

  const handleDelete = (row: Campaign) => {
    showModal({
      type: "confirm",
      title: "Delete Campaign",
      message: `Are you sure you want to delete "${row.campaign_name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/admin/email-settings/email-campaigns/${row.id}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );

          const result = await res.json();

          if (result.success) {
            fetchCampaigns();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Campaign deleted successfully.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Campaign Error:", error);
        }
      },
    });
  };

  const normalizeCampaignRow = (row: any) =>
    // console.log(row)
    ({
      ...row,
      campaign_name: row?.campaign_name ?? "",
      email_template_id:
        row?.email_template_id !== null && row?.email_template_id !== undefined
          ? String(row.email_template_id)
          : "",
      smtp_id:
        row?.smtp_id !== null && row?.smtp_id !== undefined
          ? String(row.smtp_id)
          : "",
      region_id:
        row?.region_id !== null && row?.region_id !== undefined
          ? String(row.region_id)
          : "",
      audience: row?.audience || "subscribers",
      job_id:
        row?.job_id !== null && row?.job_id !== undefined
          ? String(row.job_id)
          : "",
      application_status: row?.application_status || "",
      delivery_status: row?.delivery_status || "draft",
      status: row?.status || "active",
    });

  return (
    <div>
      <AdminListToolkit
        title="Email Campaigns"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setJobStatuses([]);
          setActiveRow({
            campaign_name: "",
            email_template_id: "",
            smtp_id: "",
            region_id: "",
            audience: "subscribers",
            job_id: "",
            application_status: "",
            delivery_status: "draft",
            status: "active",
          });
          setModalOpen(true);
        }}
        onView={(row) => {
          const normalized = normalizeCampaignRow(row);
          setModalMode("view");
          setActiveRow(normalized);

          if (normalized.audience === "job_applicants" && normalized.job_id) {
            console.log("job_applicants");
            fetchStatusesByJob(normalized.job_id);
          } else {
            setJobStatuses([]);
          }

          setModalOpen(true);
        }}
        onEdit={(row) => {
          const normalized = normalizeCampaignRow(row);
          setModalMode("edit");
          setActiveRow(normalized);

          if (normalized.audience === "job_applicants" && normalized.job_id) {
            fetchStatusesByJob(normalized.job_id);
          } else {
            setJobStatuses([]);
          }

          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <div className="algorithm-grid">
            {filteredData.map((item: Campaign) => (
              <div
                key={item.id}
                className={`algorithm-card ${
                  selectedRow?.id === item.id ? "selected" : ""
                }`}
                onClick={() => setSelectedRow(item)}
              >
                <h3>{item.campaign_name}</h3>

                <p>
                  <strong>Template:</strong> {item.template_name || "-"}
                </p>

                <p>
                  <strong>Audience:</strong> {toCaptionCase(item.audience)}
                </p>

                {item.audience === "public" && (
                  <p>
                    <strong>Region:</strong> {item.region_name || "-"}
                  </p>
                )}

                {item.audience === "job_applicants" && (
                  <>
                    <p>
                      <strong>Job ID:</strong> {item.job_id || "-"}
                    </p>
                    <p>
                      <strong>Job Title:</strong> {item.job_title || "-"}
                    </p>
                    <p>
                      <strong>Application Status:</strong>{" "}
                      {item.application_status
                        ? toCaptionCase(item.application_status)
                        : "-"}
                    </p>
                  </>
                )}

                <p>
                  <strong>Delivery Status:</strong>{" "}
                  <span
                    className={
                      item.delivery_status === "sent"
                        ? "badge-success"
                        : item.delivery_status === "failed"
                          ? "badge-danger"
                          : item.delivery_status === "partial"
                            ? "badge-warning"
                            : "badge-warning"
                    }
                  >
                    {item.delivery_status?.charAt(0).toUpperCase() +
                      item.delivery_status?.slice(1)}
                  </span>
                </p>

                <p>
                  <strong>Created At:</strong>{" "}
                  {formatDateToDMY(item.created_at)}
                </p>

                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={
                      item.status === "active"
                        ? "badge-success"
                        : "badge-danger"
                    }
                  >
                    {(item.status || "active").charAt(0).toUpperCase() +
                      item.status?.slice(1)}
                  </span>
                </p>

                <p>
                  <strong>Email From:</strong> {item.smtp_from_email || "-"}
                </p>

                <div className="campaign-email-dropdowns">
                  <div className="campaign-email-dropdown-block">
                    <button
                      type="button"
                      className="campaign-email-dropdown-toggle"
                      onClick={() => toggleDropdown(`sent-${item.id}`)}
                    >
                      <span>Recipients</span>
                      <span className="campaign-email-count">
                        {item.sent_emails?.length || 0}
                      </span>
                    </button>

                    {openDropdownId === `sent-${item.id}` && (
                      <div className="campaign-email-dropdown-list">
                        {item.sent_emails?.length ? (
                          item.sent_emails.map((email, index) => (
                            <div
                              key={`${email}-${index}`}
                              className="campaign-email-pill"
                            >
                              <span>{email}</span>
                            </div>
                          ))
                        ) : (
                          <div className="campaign-email-empty">
                            No recipients
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="campaign-email-dropdown-block">
                    <button
                      type="button"
                      className="campaign-email-dropdown-toggle"
                      onClick={() => toggleDropdown(`failed-${item.id}`)}
                    >
                      <span>Failed Emails</span>
                      <span className="campaign-email-count danger">
                        {item.failed_emails?.length || 0}
                      </span>
                    </button>

                    {openDropdownId === `failed-${item.id}` && (
                      <div className="campaign-email-dropdown-list">
                        {item.failed_emails?.length ? (
                          item.failed_emails.map((entry, index) => (
                            <div
                              key={`${entry.email}-${index}`}
                              className="campaign-email-pill danger"
                              title={entry.reason || ""}
                            >
                              <span>{entry.email}</span>
                            </div>
                          ))
                        ) : (
                          <div className="campaign-email-empty">
                            No failed emails
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        key={`${modalMode}-${activeRow?.id || "new"}`}
        isOpen={modalOpen}
        mode={modalMode}
        title="Email Campaign"
        fields={campaignFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={(formData) =>
          setActiveRow((prev: any) => ({
            ...prev,
            ...formData,
          }))
        }
        onFormChange={(formData) => {
          setActiveRow((prev: any) => {
            const merged = { ...prev, ...formData };

            const prevAudience = prev?.audience || "";
            const nextAudience = merged.audience || "";

            const prevJobId = String(prev?.job_id || "");
            const nextJobId = String(merged.job_id || "");

            const prevRegionId = String(prev?.region_id || "");
            const nextRegionId = String(merged.region_id || "");

            // prevent useless rerender loops
            const nothingChanged =
              String(prev?.campaign_name || "") ===
                String(merged?.campaign_name || "") &&
              String(prev?.email_template_id || "") ===
                String(merged?.email_template_id || "") &&
              String(prev?.smtp_id || "") === String(merged?.smtp_id || "") &&
              String(prevAudience) === String(nextAudience) &&
              String(prevJobId) === String(nextJobId) &&
              String(prevRegionId) === String(nextRegionId) &&
              String(prev?.application_status || "") ===
                String(merged?.application_status || "") &&
              String(prev?.delivery_status || "") ===
                String(merged?.delivery_status || "") &&
              String(prev?.status || "") === String(merged?.status || "");

            if (nothingChanged) return prev;

            // PUBLIC audience
            if (nextAudience === "public") {
              if (prevAudience !== "public") {
                setJobStatuses([]);
                return {
                  ...merged,
                  job_id: "",
                  application_status: "",
                };
              }

              return merged;
            }

            // JOB APPLICANTS audience
            if (nextAudience === "job_applicants") {
              if (prevAudience !== "job_applicants") {
                return {
                  ...merged,
                  region_id: "",
                  application_status: "",
                };
              }

              if (nextJobId !== prevJobId) {
                fetchStatusesByJob(nextJobId);
                return {
                  ...merged,
                  region_id: "",
                  application_status: "",
                };
              }

              return {
                ...merged,
                region_id: "",
              };
            }

            // all other audiences
            if (prevAudience === "job_applicants") {
              setJobStatuses([]);
            }

            return {
              ...merged,
              region_id: "",
              job_id: "",
              application_status: "",
            };
          });
        }}
        hideDefaultSubmit
        footer={
          modalMode !== "view" ? (
            <>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={(e) => handleSaveCampaign(activeRow, e)}
                className="btn-secondary"
                style={{ background: "#10b981" }}
                disabled={loading}
              >
                Save
              </button>

              {String(activeRow?.delivery_status || "").toLowerCase() ===
                "sent" && (
                <button
                  type="button"
                  onClick={() => handleSendCampaign(activeRow)}
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Email"}
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="btn-secondary"
            >
              Close
            </button>
          )
        }
      />
    </div>
  );
}
